import { FILENAME_MATCH_RANK } from './constants/filename-match-rank'
import type { FilenameMatchRank } from './types/filename-match-rank'

/** Query words this long or longer may match with typos; shorter ones must match exactly. */
const MIN_FUZZY_WORD_LENGTH = 4
/** Query words this long or longer tolerate two edits; shorter fuzzy words tolerate one. */
const TWO_EDIT_WORD_LENGTH = 8

/** A trailing image-file extension — not part of what a user searches for. Only known image
 *  types: a looser "any short dotted suffix" would also cut real words and numbers
 *  (`Sunset.Beach`, `Pattern 3.25`). */
const EXTENSION_PATTERN = /\.(?:jpe?g|png|gif|tiff?|webp|bmp|svg)$/

/** Lower-cases `value` and drops a trailing file extension. */
function baseName(value: string): string {
  return value.trim().toLowerCase().replace(EXTENSION_PATTERN, '')
}

/** The letters and digits only — so `ba-1103`, `ba_1103` and `ba 1103` all read `ba1103`. */
function compact(value: string): string {
  return value.replace(/[^\p{L}\p{N}]+/gu, '')
}

/** Words split on anything that isn't a letter or digit. */
function words(value: string): string[] {
  return value.split(/[^\p{L}\p{N}]+/u).filter(Boolean)
}

/**
 * The fewest single-character edits (substitute, insert, delete, or swap two neighbors) that turn
 * `pattern` into some substring of `text` — so a query word can match anywhere in a filename,
 * including inside a longer word. Optimal-string-alignment distance with a free start and end in
 * `text` (Sellers' approximate substring matching).
 */
function substringEditDistance(pattern: string, text: string): number {
  const m = pattern.length
  const n = text.length

  if (m === 0) return 0

  // rows[i][j]: best distance for pattern[0..i) ending at text[0..j). Row 0 is all zero: the match
  // may start anywhere in `text`.
  const rows: number[][] = [new Array<number>(n + 1).fill(0)]

  for (let i = 1; i <= m; i += 1) {
    const row = new Array<number>(n + 1)

    row[0] = i
    for (let j = 1; j <= n; j += 1) {
      const cost = pattern[i - 1] === text[j - 1] ? 0 : 1

      row[j] = Math.min(rows[i - 1][j] + 1, row[j - 1] + 1, rows[i - 1][j - 1] + cost)

      if (i > 1 && j > 1 && pattern[i - 1] === text[j - 2] && pattern[i - 2] === text[j - 1]) {
        row[j] = Math.min(row[j], rows[i - 2][j - 2] + 1)
      }
    }

    rows.push(row)
  }

  return Math.min(...rows[m])
}

/**
 * How many edits a query word may be off by: none for words with a digit (product codes and
 * numbers are exact — `ba1103` must not find `ba1108`) or under 4 characters, then one, then two.
 */
function allowedEdits(word: string): number {
  if (/\d/.test(word)) return 0
  if (word.length >= TWO_EDIT_WORD_LENGTH) return 2
  if (word.length >= MIN_FUZZY_WORD_LENGTH) return 1

  return 0
}

/**
 * How closely a background's filename matches the "Search by filename" term, or null when it
 * doesn't match at all. Tried strictest first (see `FILENAME_MATCH_RANK`):
 *
 * 1. `exact` — the term appears as typed (case-insensitive), the original behavior.
 * 2. `normalized` — it appears once spaces, punctuation (`-`, `_`, `.`, `'`) and the file
 *    extension are ignored on both sides: `ba-1103` finds `BA 1103.jpg`, `marys` finds
 *    `St. Mary's`.
 * 3. `words` — every word of the term starts a word of the name, in any order: `sun ba` finds
 *    `BA1103 Sunset`.
 * 4. `fuzzy` — every word of the term is within a few typos of part of the name: one edit for
 *    words of 4–7 characters, two for 8 or more, none for shorter words (which must appear
 *    exactly, as must any word containing a digit), so `sunest` and `sunsat` find
 *    `BA1103 Sunset` but `ba1108` doesn't. An edit is a wrong, missing, extra or swapped
 *    character.
 *
 * A multi-word term must match on every word, so adding words narrows the results.
 */
export function matchBackgroundFilename(
  displayName: string,
  query: string
): FilenameMatchRank | null {
  const term = query.trim().toLowerCase()

  if (!term) return null

  const name = displayName.toLowerCase()

  if (name.includes(term)) return FILENAME_MATCH_RANK.Exact

  const nameBase = baseName(displayName)
  const nameCompact = compact(nameBase)
  const termCompact = compact(baseName(term))

  if (!termCompact || !nameCompact) return null
  if (nameCompact.includes(termCompact)) return FILENAME_MATCH_RANK.Normalized

  const termWords = words(baseName(term))
  const nameWords = words(nameBase)

  if (termWords.every((word) => nameWords.some((nameWord) => nameWord.startsWith(word)))) {
    return FILENAME_MATCH_RANK.Words
  }

  const everyWordClose = termWords.every((word) => {
    const edits = allowedEdits(word)

    return edits === 0
      ? nameCompact.includes(word)
      : substringEditDistance(word, nameCompact) <= edits
  })

  return everyWordClose ? FILENAME_MATCH_RANK.Fuzzy : null
}
